import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowCounterClockwise, CaretRight, ChatCircleDots, FileText, Headset,
  House, Info, MapPin, Package, ShieldCheck, Storefront, Truck, UserCircle, X,
} from "@phosphor-icons/react";
import "./mobile-navigation.css";

const navigationGroups = [
  { title: "Your account", items: [
    { label: "Home", to: "/home", icon: House },
    { label: "My Orders", to: "/orders", icon: Package, protected: true },
    { label: "My Account", to: "/my-account", icon: UserCircle, protected: true },
    { label: "Saved Addresses", to: "/address-list", icon: MapPin, protected: true },
  ] },
  { title: "Help & Support", items: [
    { label: "Help Center", to: "/help-center", icon: Headset },
    { label: "Contact Us", to: "/contact-us", icon: ChatCircleDots },
    { label: "Free Delivery", to: "/free-delivery", icon: Truck },
    { label: "Returns Policy", to: "/returns-policy", icon: ArrowCounterClockwise },
  ] },
  { title: "About & Policies", items: [
    { label: "About Us", to: "/about-page", icon: Info },
    { label: "Terms & Conditions", to: "/terms-and-conditions", icon: FileText },
    { label: "Privacy Policy", to: "/privacy-policy", icon: ShieldCheck },
  ] },
];

const MobileNavigation = ({ open, onClose, guestUser, onLoginRequired, marketLabel, currency }) => {
  const { pathname } = useLocation();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
      if (event.key === "Tab") {
        const controls = [...dialogRef.current.querySelectorAll("a[href], button:not(:disabled)")];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!controls.includes(document.activeElement) || (event.shiftKey && document.activeElement === first)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    const handleResize = () => {
      if (window.matchMedia("(min-width: 992px)").matches) onClose();
    };
    document.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("resize", handleResize);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("resize", handleResize);
      previousFocus?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleLinkClick = (event, item) => {
    onClose();
    if (item.protected && guestUser) onLoginRequired(event);
  };

  return (
    <div className="rn-mobile-navigation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) {
        event.preventDefault();
        onClose();
      }
    }}>
      <aside id="mobile-navigation" className="rn-mobile-navigation__panel" ref={dialogRef}
        role="dialog" aria-modal="true" aria-label="Main menu">
        <div className="rn-mobile-navigation__header">
          <button ref={closeRef} type="button" className="rn-mobile-navigation__close" onClick={onClose} aria-label="Close menu">
            <X size={20} aria-hidden="true" />
          </button>
          <Link to="/home" aria-label="RGHT NOW home" onClick={onClose} className="rn-mobile-navigation__logo">
            <img src={`${process.env.PUBLIC_URL}/assets/images/logo/rghtnow-transparant-logo.png`} alt="RGHT NOW" />
          </Link>
          <p>Your neighbourhood, delivered.</p>
          <span className="rn-mobile-navigation__market"><MapPin size={14} aria-hidden="true" />{marketLabel} · {currency}</span>
        </div>
        <div className="rn-mobile-navigation__content">
          <nav aria-label="Main navigation">
            {navigationGroups.map((group) => (
              <section className="rn-mobile-navigation__group" key={group.title} aria-label={group.title}>
                <h2>{group.title}</h2>
                <ul>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = pathname === item.to;
                    return (
                      <li key={item.to}>
                        <Link to={item.to} className={`rn-mobile-navigation__link${active ? " is-active" : ""}`}
                          aria-current={active ? "page" : undefined} onClick={(event) => handleLinkClick(event, item)}>
                          <span className="rn-mobile-navigation__icon"><Icon size={21} weight="duotone" aria-hidden="true" /></span>
                          <span>{item.label}</span>
                          <CaretRight className="rn-mobile-navigation__arrow" size={15} aria-hidden="true" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </nav>
          <Link to="/onboard-your-store" className="rn-mobile-navigation__seller" onClick={onClose}>
            <span className="rn-mobile-navigation__seller-icon"><Storefront size={26} weight="duotone" aria-hidden="true" /></span>
            <span><strong>Become a Seller</strong><small>Grow your business with RGHT NOW</small></span>
            <CaretRight size={17} aria-hidden="true" />
          </Link>
          <p className="rn-mobile-navigation__footer">RGHT NOW © {new Date().getFullYear()}</p>
        </div>
      </aside>
    </div>
  );
};

export default MobileNavigation;
